/**
 * Hook that provides event handlers for instance interactions.
 * Extracted from InstancesPanel to reduce component complexity.
 */

import React, { startTransition, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setDraggingComponent,
  setHoveredElementKey,
  setSelectedElementKey,
} from "@/store/iframeSlice";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { setTempoClipboardDataOnDrag } from "../../Canvas/utils/canvasClipboardData";
import { TempoElement } from "@/utils/tempoElement";
import type { Instance } from "./instancesStore";

export interface UseInstanceEventHandlersOptions {
  canvasId: string | undefined;
  selectedPageId: string | null | undefined;
  selectedAssetCodebaseId: string;
  captureInstance: (instance: Instance) => Promise<void>;
}

export interface InstanceEventHandlers {
  handleDragStart: (e: React.DragEvent, instance: Instance) => void;
  handleDragEnd: () => void;
  handleMouseEnter: (instance: Instance) => void;
  handleMouseLeave: () => void;
  handleClick: (instance: Instance) => void;
  handleDoubleClick: (instance: Instance) => void;
}

/**
 * Provides event handlers for instance card interactions:
 * - Drag and drop with clipboard data
 * - Hover highlighting
 * - Click selection
 * - Double-click navigation
 */
export function useInstanceEventHandlers({
  canvasId,
  selectedPageId,
  selectedAssetCodebaseId,
  captureInstance,
}: UseInstanceEventHandlersOptions): InstanceEventHandlers {
  const dispatch = useDispatch();

  const handleDragStart = useCallback(
    (e: React.DragEvent, instance: Instance) => {
      e.dataTransfer.dropEffect = "move";
      dispatch(setDraggingComponent("component-instance"));

      const { dragData, contentWindowData } = instance;

      // If we have precomputed fileContents, use that (supports children)
      if (dragData.fileContents) {
        setTempoClipboardDataOnDrag(e, {
          fileContents: dragData.fileContents,
          fileContentsSourceFilename: dragData.fileContentsSourceFilename,
          compressedNavTreeNodesForInstantUpdate: dragData.compressedNavTree
            ? [dragData.compressedNavTree]
            : undefined,
          insertIntoHoveredElement: true,
          width: contentWindowData.width,
          height: contentWindowData.height,
        });
      } else {
        // Fallback: use the component definition's codebaseId
        setTempoClipboardDataOnDrag(e, {
          insertCodebaseId: selectedAssetCodebaseId,
          insertIntoHoveredElement: true,
          propsToSet: instance.props,
          width: contentWindowData.width,
          height: contentWindowData.height,
        });
      }
    },
    [dispatch, selectedAssetCodebaseId],
  );

  const handleDragEnd = useCallback(() => {
    dispatch(setDraggingComponent(null));
  }, [dispatch]);

  const handleMouseEnter = useCallback(
    (instance: Instance) => {
      const elementKey = new TempoElement(
        instance.instanceCodebaseId,
        instance.storyboardId,
        instance.uniquePath,
      ).getKey();
      startTransition(() => {
        dispatch(setHoveredElementKey(elementKey));
      });
    },
    [dispatch],
  );

  const handleMouseLeave = useCallback(() => {
    startTransition(() => {
      dispatch(setHoveredElementKey(null));
    });
  }, [dispatch]);

  const handleClick = useCallback(
    (instance: Instance) => {
      const elementKey = new TempoElement(
        instance.instanceCodebaseId,
        instance.storyboardId,
        instance.uniquePath,
      ).getKey();
      dispatch(setSelectedElementKey(elementKey));
      startTransition(() => {
        captureInstance(instance);
      });
    },
    [dispatch, captureInstance],
  );

  const handleDoubleClick = useCallback(
    (instance: Instance) => {
      if (!canvasId) return;

      // If instance is on a different page, switch pages first
      if (instance.pageId && instance.pageId !== selectedPageId) {
        usePagesStore.getState().setSelectedPageId(canvasId, instance.pageId);
      }

      updateCanvasStoryboards(canvasId, [
        {
          changeType: "update",
          storyboardId: instance.storyboardId,
          pageId: instance.pageId,
          storyboardUpdateDict: {
            jumpToStoryboard: true,
          },
        },
      ]);

      // Select the instance after the jump (deferred to ensure canvas is ready)
      requestAnimationFrame(() => {
        const elementKey = new TempoElement(
          instance.instanceCodebaseId,
          instance.storyboardId,
          instance.uniquePath,
        ).getKey();
        dispatch(setSelectedElementKey(elementKey));
      });
    },
    [canvasId, dispatch, selectedPageId],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    handleDoubleClick,
  };
}
